// Session state for the SPA. Production holds the Cognito ID token in
// MEMORY ONLY (Security Ops §4.1 token-storage rule). Browser refresh
// wipes it; the httpOnly refresh-token cookie + POST /auth/refresh
// rebuild a fresh ID token on reload via api/client.ts's 401 → refresh
// → retry path.
//
// The refresh token NEVER touches JS — it lives only in the httpOnly
// cookie set by /auth/login and consumed by /auth/refresh.

const LOCAL_SESSION_STORAGE_KEY = "riot_local_session";

interface StoredLocalSession {
  idToken?: string;
  accessToken?: string | null;
}

function isLocalOrigin(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
}

function readStoredLocalSession(): StoredLocalSession {
  if (!isLocalOrigin()) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(LOCAL_SESSION_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as StoredLocalSession;
    return typeof parsed.idToken === "string" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStoredLocalSession(token: string | null, access: string | null): void {
  if (!isLocalOrigin()) {
    return;
  }
  try {
    if (!token) {
      window.localStorage.removeItem(LOCAL_SESSION_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      LOCAL_SESSION_STORAGE_KEY,
      JSON.stringify({ idToken: token, accessToken: access }),
    );
  } catch {
    // Ignore storage failures; local dev can still rely on the in-memory token.
  }
}

const storedLocalSession = readStoredLocalSession();

let idToken: string | null = storedLocalSession.idToken ?? null;
// Cognito access token. In production it is held alongside the ID
// token in memory only. On localhost we persist it with the ID token
// so the local proxy workflow survives reloads. Required for the
// per-user Cognito APIs the SPA invokes from Settings (passkey
// registration / list / delete) — Phase B Task 7. The ID token
// continues to ride in Authorization for normal data-API requests
// because the API Gateway authorizer reads custom: claims that only
// ride on the ID token.
let accessToken: string | null = storedLocalSession.accessToken ?? null;
const DEV_BYPASS_STORAGE_KEY = "riot_dev_signed_in";
const ACTING_TENANT_STORAGE_KEY = "riot_acting_tenant_id";
const ACTING_COMPANY_STORAGE_KEY = "riot_acting_company_name";

// Browser dev-mode bypass: skip real auth in local MSW sessions so the
// whole app is explorable from a clean checkout. Tests stay on the
// normal code paths so fetch/auth behavior remains verifiable.
const DEV_BYPASS_ENABLED = import.meta.env.DEV && import.meta.env.MODE !== "test";
let devSignedIn = readStoredDevSignedIn();
let actingTenantId = readStoredValue(ACTING_TENANT_STORAGE_KEY);
let actingCompanyName = readStoredValue(ACTING_COMPANY_STORAGE_KEY);

function readStoredValue(key: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return (isLocalOrigin() ? window.localStorage : window.sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function readStoredDevSignedIn(): boolean {
  if (!DEV_BYPASS_ENABLED || typeof window === "undefined") {
    return false;
  }

  try {
    const stored = window.sessionStorage.getItem(DEV_BYPASS_STORAGE_KEY);
    return stored === null ? true : stored === "1";
  } catch {
    return true;
  }
}

function setStoredDevSignedIn(value: boolean): void {
  devSignedIn = value;

  if (!DEV_BYPASS_ENABLED || typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(DEV_BYPASS_STORAGE_KEY, value ? "1" : "0");
  } catch {
    // Ignore storage failures; local dev can still rely on the in-memory flag.
  }
}

function setStoredActingContext(tenantId: string | null, companyName: string | null): void {
  actingTenantId = tenantId;
  actingCompanyName = companyName;

  if (typeof window === "undefined") {
    return;
  }

  try {
    const storage = isLocalOrigin() ? window.localStorage : window.sessionStorage;
    if (tenantId) {
      storage.setItem(ACTING_TENANT_STORAGE_KEY, tenantId);
    } else {
      storage.removeItem(ACTING_TENANT_STORAGE_KEY);
    }

    if (companyName) {
      storage.setItem(ACTING_COMPANY_STORAGE_KEY, companyName);
    } else {
      storage.removeItem(ACTING_COMPANY_STORAGE_KEY);
    }
  } catch {
    // Ignore storage failures; the in-memory values still apply for this tab.
  }
}

function shouldUseDevBypass(): boolean {
  return DEV_BYPASS_ENABLED && devSignedIn && !idToken;
}

function parseTokenPayload(token: string): Record<string, unknown> | null {
  if (typeof atob !== "function") {
    return null;
  }

  try {
    const encoded = token.split(".")[1];
    if (!encoded) {
      return null;
    }

    const normalised = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalised.padEnd(Math.ceil(normalised.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function getTokenPayload(): Record<string, unknown> | null {
  if (idToken) {
    return parseTokenPayload(idToken);
  }
  return null;
}

// setSession stores the ID token returned by /auth/login or /auth/refresh.
// Pass null to drop the in-memory token (e.g. on logout or 401 with no
// refresh available). Optional second argument also stores the Cognito
// access token, used by the WebAuthn management endpoints.
export function setSession(token: string | null, access: string | null = null): void {
  idToken = token;
  accessToken = access;
  writeStoredLocalSession(token, access);
  if (token && DEV_BYPASS_ENABLED) {
    setStoredDevSignedIn(true);
  }
}

// setRefreshedToken is a thin alias kept for the api/client.ts 401-retry
// path. New callers should prefer setSession.
export function setRefreshedToken(token: string | null, access: string | null = null): void {
  setSession(token, access);
}

export function clearSession(): void {
  idToken = null;
  accessToken = null;
  writeStoredLocalSession(null, null);
  setStoredActingContext(null, null);
}

// getAccessToken returns the Cognito access token currently held in
// memory. Required by the WebAuthn management endpoints (Settings →
// passkeys). Null if the user signed in before access-token plumbing
// landed (refresh on next /auth/refresh) or isn't signed in.
export function getAccessToken(): string | null {
  return accessToken;
}

export function isDevAuthEnabled(): boolean {
  return DEV_BYPASS_ENABLED;
}

export function completeDevSignIn(): void {
  if (DEV_BYPASS_ENABLED) {
    setStoredDevSignedIn(true);
  }
}

export function completeDevSignOut(): void {
  clearSession();
  if (DEV_BYPASS_ENABLED) {
    setStoredDevSignedIn(false);
  }
}

export function startActingAsCompany(tenantId: string, companyName?: string): void {
  setStoredActingContext(tenantId, companyName ?? null);
}

export function stopActingAsCompany(): void {
  setStoredActingContext(null, null);
}

export function getActingTenantId(): string | null {
  return actingTenantId;
}

export function getActingCompanyName(): string | null {
  return actingCompanyName;
}

export function isActingAsCompany(): boolean {
  return !!actingTenantId;
}

// getIdToken returns the current ID token if it's still valid. The
// JWT's `exp` claim is checked locally — the API still re-validates
// every request, but skipping known-expired tokens saves a round-trip.
export function getIdToken(): string | null {
  if (idToken) {
    const payload = parseTokenPayload(idToken);
    const exp = typeof payload?.exp === "number" ? payload.exp : 0;
    if (exp > 0 && exp * 1000 <= Date.now()) {
      writeStoredLocalSession(null, null);
      return null;
    }
    return idToken;
  }
  if (shouldUseDevBypass()) return "dev-bypass-token";
  return null;
}

export function getTenantId(): string | null {
  if (actingTenantId) {
    return actingTenantId;
  }

  const payload = getTokenPayload();
  if (!payload) {
    return shouldUseDevBypass() ? "dev-tenant-00000000" : null;
  }
  const tenantId = payload["custom:tenantId"];
  return typeof tenantId === "string" ? tenantId : null;
}

export function getRole(): string | null {
  const payload = getTokenPayload();
  if (!payload) {
    return shouldUseDevBypass() ? "super_admin" : null;
  }
  const role = payload["custom:role"];
  return typeof role === "string" ? role : null;
}

// Roles in ascending order of access.
const ROLE_RANK: Record<string, number> = {
  viewer: 0,
  operator: 1,
  tenant_admin: 2,
  super_admin: 3,
};

// hasMinRole says whether the signed-in user holds minRole or above. No
// session, or a role this build doesn't know, ranks as viewer. The server
// enforces the same order; this only decides what the UI offers.
export function hasMinRole(minRole: string): boolean {
  return (ROLE_RANK[getRole() ?? "viewer"] ?? 0) >= (ROLE_RANK[minRole] ?? 0);
}
